<template>
  <v-card 
    variant="outlined" 
    class="quoted-post-card rounded-lg border border-white-05 bg-black-05" 
    @click.stop="handleQuoteClick"
  >
    <div v-if="record.$type === 'app.bsky.embed.record#viewRecord' || record.$type === 'app.bsky.embed.record#view'">
      <div class="pa-3">
        <div class="d-flex align-center mb-1 overflow-hidden">
          <v-avatar size="18" class="mr-3">
            <v-img v-if="author?.avatar" :src="author.avatar"></v-img>
            <v-icon v-else icon="mdi-account" size="14"></v-icon>
          </v-avatar>
          <span class="text-caption font-weight-bold text-white text-truncate flex-shrink-1" style="max-width: 200px;">{{ author?.displayName || author?.handle }}</span>
          &nbsp;
          <span class="text-caption text-grey flex-shrink-0 text-truncate" style="max-width: 120px;">@{{ author?.handle }}</span>
          &nbsp;
          <span class="text-caption text-grey flex-shrink-0">·</span>
          &nbsp;
          <v-tooltip location="bottom" open-delay="500">
            <template v-slot:activator="{ props }">
              <span 
                v-bind="props"
                class="text-caption text-grey flex-shrink-0"
              >
                {{ formatDate(indexedAt) }}
              </span>
            </template>
            <span>{{ new Date(indexedAt).toLocaleString() }}</span>
          </v-tooltip>
        </div>
        
        <div class="text-caption line-clamp-3" v-html="renderContent(record.value?.text || record.record?.text || record.text || record.record?.value?.text)">
        </div>

        <!-- Compact Media within Quote -->
        <div v-if="quoteMedia" class="mt-2 rounded overflow-hidden">
          <div v-if="quoteMedia.$type === 'app.bsky.embed.images#view'" class="quote-image-grid">
            <v-img
              v-for="img in quoteMedia.images"
              :key="img.thumb"
              :src="isGif(img.thumb) ? (img.fullsize || getBetterImageUrl(img.thumb)) : getBetterImageUrl(img.thumb)"
              height="100"
              cover
              class="position-relative"
            >
              <div v-if="isGif(img.thumb)" class="gif-badge">GIF</div>
            </v-img>
          </div>
          <div v-else-if="quoteMedia.$type === 'app.bsky.embed.video#view'" class="quote-video-preview">
            <video
              ref="videoRef"
              autoplay
              loop
              muted
              playsinline
              :poster="quoteMedia.thumbnail"
              class="w-100 d-block rounded"
              style="max-height: 300px; background: black;"
            >
              <source :src="quoteMedia.playlist" type="application/x-mpegURL">
            </video>
          </div>
          <div v-else-if="quoteMedia.$type === 'app.bsky.embed.external#view'">
            <!-- GIF Embed Style for Quote (No Metadata) -->
            <v-card 
              v-if="isGif('', quoteMedia.external)"
              variant="flat" 
              class="border border-white-05 bg-black overflow-hidden mb-2" 
              :href="quoteMedia.external.uri" 
              target="_blank"
              @click.stop
            >
            <v-img
              v-if="quoteMedia.external.thumb || quoteMedia.external.uri" 
              :src="getBetterImageUrl(quoteMedia.external.thumb, quoteMedia.external.uri)" 
              max-height="300" 
              class="position-relative"
            >
              <div class="gif-badge">GIF</div>
            </v-img>
            </v-card>

            <!-- Standard External Link for Quote -->
            <div v-else class="quote-external border border-white-05 pa-2 d-flex align-center bg-black-05">
              <v-img v-if="quoteMedia.external.thumb" :src="quoteMedia.external.thumb" width="40" height="40" cover class="rounded mr-2"></v-img>
              <div class="flex-grow-1 overflow-hidden">
                <div class="text-caption font-weight-bold text-truncate">{{ quoteMedia.external.title }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Handle deleted or blocked records -->
    <div v-else class="pa-3 text-caption text-grey font-italic bg-black-05">
      <div v-if="record.$type === 'app.bsky.embed.record#viewNotFound'">
        <v-icon icon="mdi-alert-circle-outline" size="small" class="mr-1"></v-icon>
        Post not found
      </div>
      <div v-else-if="record.$type === 'app.bsky.embed.record#viewBlocked'">
        <v-icon icon="mdi-block-helper" size="small" class="mr-1"></v-icon>
        Post blocked
      </div>
      <div v-else>
        Embedded record
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { renderRichText } from '@/utils/richtext';

const props = defineProps<{
  record: any;
}>();

const router = useRouter();
const videoRef = ref<HTMLVideoElement | null>(null);
let hlsInstance: any = null;

// Normalize the record view which can be nested differently
const viewRecord = computed(() => {
  // If it already has the structure of a viewRecord, return it
  if (props.record.$type === 'app.bsky.embed.record#viewRecord') return props.record;
  if (props.record.record?.$type === 'app.bsky.embed.record#viewRecord') return props.record.record;
  
  // If it's a full post object (what we emit when quoting), it has the same fields 
  // but might lack the $type or have different nesting
  if (props.record.author && props.record.uri) return props.record;
  
  return null;
});

const author = computed(() => viewRecord.value?.author);
const indexedAt = computed(() => viewRecord.value?.indexedAt || viewRecord.value?.record?.indexedAt);
const quoteMedia = computed(() => {
  const v = viewRecord.value;
  if (!v) return null;
  
  // Standard viewRecord uses embeds array, full post uses embed object
  let embed = v.embeds?.[0] || v.embed;
  if (!embed) return null;

  // Handle recordWithMedia by extracting the actual media part
  if (embed.$type === 'app.bsky.embed.recordWithMedia#view') {
    return embed.media;
  }
  
  return embed;
});

const initHls = () => {
  const video = videoRef.value;
  if (!video) return;

  const src = quoteMedia.value?.playlist;
  if (!src || !src.endsWith('.m3u8')) return;

  const run = () => {
    if (!videoRef.value) return;

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
    } else if ((window as any).Hls) {
      const Hls = (window as any).Hls;
      if (Hls.isSupported()) {
        if (hlsInstance) hlsInstance.destroy();
        hlsInstance = new Hls();
        hlsInstance.loadSource(src);
        hlsInstance.attachMedia(video);
        hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(e => console.warn('Autoplay failed:', e));
        });
      }
    } else {
      setTimeout(run, 200);
    }
  };
  run();
};

onMounted(() => {
  nextTick(() => {
    initHls();
  });
});

onBeforeUnmount(() => {
  if (hlsInstance) {
    hlsInstance.destroy();
  }
});

watch(() => quoteMedia.value, () => {
  nextTick(() => {
    initHls();
  });
}, { deep: true });


const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 0) return 'now'; 
  if (diffInSeconds < 60) return `${diffInSeconds}s`;
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const renderContent = (text: string) => {
  if (!text) return '';
  const v = viewRecord.value;
  const facets = v?.value?.facets ?? v?.record?.facets ?? v?.facets ?? v?.record?.value?.facets ?? [];
  return renderRichText(text, facets, { onlyFacets: true });
};

const handleQuoteClick = () => {
  if (!viewRecord.value) return;
  const uri = viewRecord.value.uri;
  const handle = author.value?.handle;
  if (!uri || !handle) return;
  
  const rkey = uri.split('/').pop();
  router.push(`/profile/${handle}/post/${rkey}`);
};

const isGif = (url: string, external?: any) => {
  const isGifUrl = url?.toLowerCase().includes('gif') || url?.toLowerCase().endsWith('.gif');
  if (isGifUrl) return true;
  
  const thumb = external?.thumb || url;
  if (thumb?.toLowerCase().includes('media.tenor.com') || thumb?.toLowerCase().includes('giphy.com')) return true;

  if (external?.uri) {
    const uri = external.uri.toLowerCase();
    if (uri.includes('tenor.com') || uri.includes('giphy.com')) return true;
  }
  
  return false;
};

const getBetterImageUrl = (url: string, uri?: string) => {
  // If the URI itself is a direct GIF (common for Tenor), use it!
  if (uri?.toLowerCase().endsWith('.gif') || uri?.toLowerCase().includes('.gif?')) {
    return uri;
  }

  if (!url) return url;
  
  // Convert static Tenor thumbnails to GIFs
  if (url.includes('media.tenor.com')) {
    if (url.includes('/images/')) {
       return url.replace('/images/', '/').replace(/\.(png|jpe?g)$/, '.gif');
    }
    return url.replace(/\.(png|jpe?g)$/, '.gif');
  }
  
  if (url.includes('giphy.com')) {
    return url.replace(/\.(png|jpe?g)$/, '.gif');
  }
  return url;
};
</script>

<style scoped>
.quoted-post-card {
  transition: background-color 0.2s;
  cursor: pointer;
  width: 100%;
}
.quoted-post-card:hover {
  background-color: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.2) !important;
}
.bg-black-05 {
  background-color: rgba(0, 0, 0, 0.2) !important;
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 6;
  line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.quote-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 4px;
}
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.hashtag) {
  color: rgb(var(--v-theme-primary)) !important;
  text-decoration: none;
  font-weight: 500;
}

:deep(.hashtag:hover) {
  text-decoration: underline !important;
  opacity: 0.8;
}

:deep(.mention) {
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 500;
}


.gif-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 1;
}
</style>
